import React, { useMemo } from 'react';
// components
import {
    BadgeProps,
    Header,
    FeedbackIcon,
    MenuItemProps,
} from '../components';

// assets (menu icons)
import {
    EndChatIcon,
    MuteIcon,
    ThemeIcon,
    UnmuteIcon,
} from '../components';

import { useTheme } from '@amzn/ring-cs-chatbot-widget';
import { translated } from '../utils/TranslationUtil';
import { useSessionContext } from '../utils/SessionContext';

type ChatHeaderProps = {
    disabled?: boolean;
    endChat: () => void;
    isMuted?: boolean;
    sendSurvey: () => void;
    showSurvey: boolean;
    toggleMute: () => void;
    onToggleWidget: () => void;
    menuPortalElement?: HTMLDivElement;
};

const ChatHeader: React.FC<ChatHeaderProps> = ({
    endChat,
    disabled = false,
    isMuted,
    sendSurvey,
    showSurvey,
    toggleMute,
    onToggleWidget,
    menuPortalElement,
}) => {
    const { enableBot } = useSessionContext();
    const { toggleThemePreference } = useTheme();

    const badgeProps: BadgeProps = {
        title: disabled ? translated('Components.Header.badgeDisabled') : translated('Components.Header.badgeTitle'),
        type: 'solid',
        color: disabled ? 'neutral' : 'positive',
        disabled: disabled,
    };

    const menuItems: MenuItemProps[] = useMemo(() => {
        return [
            {
                key: isMuted ? 'unmute' : 'mute',
                icon: isMuted ? <UnmuteIcon /> : <MuteIcon />,
                label: isMuted
                    ? translated('Components.Header.unmuteLabel')
                    : translated('Components.Header.muteLabel'),
                onClick: toggleMute,
                closeMenu: false,
            },
            {
                key: 'theme',
                icon: <ThemeIcon />,
                label: translated('Components.Header.themeLabel'),
                onClick: toggleThemePreference,
                closeMenu: false,
            },
            // Conditionally include the feedback menu item
            showSurvey && sendSurvey
                ? {
                    key: 'feedback',
                    icon: <FeedbackIcon />,
                    label: translated('Components.Header.feedbackLabel'),
                    onClick: sendSurvey,
                    closeMenu: true,
                }
                : null,
            {
                key: 'endChat',
                icon: <EndChatIcon />,
                label: translated('Components.Header.endLabel'),
                onClick: endChat,
                closeMenu: true,
            },
        ].filter(Boolean) as MenuItemProps[];
    }, [
        isMuted,
        toggleMute,
        toggleThemePreference,
        showSurvey,
        sendSurvey,
        endChat,
    ]);

    return <Header
        ariaLabelMenu={translated('Components.Header.ariaLabelMenu')}
        ariaLabelMinimize={translated('Components.Header.ariaLabelMinimize')}
        onCollapse={onToggleWidget}
        menuItems={menuItems}
        badgeProps={badgeProps}
        menuPortalElement={menuPortalElement}
        showBadge={enableBot}
    />;
};

export default ChatHeader;
